import { taxonomyProvider } from './taxonomy-provider';
import { orgUnitsProvider } from './org-units-provider';
import { createRestProvider } from './api-rest-provider';
import type { DataProvider } from 'react-admin';

const restProvider = createRestProvider();

/**
 * Resources routed to the orchestra-api REST backend.
 * These entities require RBAC, versioning, provenance, and server-side
 * business logic that cannot be bypassed via direct DynamoDB writes.
 * Sub-paths (e.g. score_tables/generate) match via the root segment.
 */
const REST_RESOURCES = new Set([
  'courses', 'instances', 'student_groups', 'surveys', 'assignments',
  'rankings', 'instructors', 'programs',
  'events',
  'score_tables',
  'attempts',
  'students',
  'assessments',
  'tallies',
  'users',
  'role_assignments',
  'licenses',
  'teams',
  'wearable_consents',
  'biometrics',
  'mos_profiles',
]);

const isRestResource = (resource: string): boolean =>
  REST_RESOURCES.has(resource) || REST_RESOURCES.has(resource.split('/')[0]);

const retiredResource = (resource: string): never => {
  throw new Error(`The generic ${resource} data contract is retired; use the governed participation workflow`);
};

const rejectRetiredResource = (resource: string): void => {
  if (resource === 'disqualifications' || resource.startsWith('disqualifications/')) {
    retiredResource(resource);
  }
};

/**
 * Combined data provider:
 * - org_units        -> orgUnitsProvider (REST, org-unit-aware URL encoding)
 * - REST_RESOURCES   -> restProvider (orchestra-api, Bearer auth, RBAC)
 * - taxonomy resources -> governed nested REST contracts
 * - unknown resources -> explicit error; no browser AWS fallback
 */
const dynamoProvider: DataProvider = {
  getList: async (resource, params) => {
    rejectRetiredResource(resource);
    if (resource === 'taxonomies' || resource === 'taxonomy_terms') return taxonomyProvider.getList(resource, params);
    if (resource === 'org_units') return orgUnitsProvider.getList(resource, params);
    if (isRestResource(resource)) return restProvider.getList(resource, params);
    throw new Error(`No authorized API contract for resource: ${resource}`);
  },
  getOne: async (resource, params) => {
    rejectRetiredResource(resource);
    if (resource === 'taxonomies' || resource === 'taxonomy_terms') return taxonomyProvider.getOne(resource, params);
    if (resource === 'org_units') return orgUnitsProvider.getOne(resource, params);
    if (isRestResource(resource)) return restProvider.getOne(resource, params);
    throw new Error(`No authorized API contract for resource: ${resource}`);
  },
  getMany: async (resource, params) => {
    rejectRetiredResource(resource);
    if (resource === 'taxonomies' || resource === 'taxonomy_terms') return taxonomyProvider.getMany(resource, params);
    if (resource === 'org_units') return orgUnitsProvider.getMany(resource, params);
    if (isRestResource(resource)) return restProvider.getMany(resource, params);
    throw new Error(`No authorized API contract for resource: ${resource}`);
  },
  getManyReference: async (resource, params) => {
    rejectRetiredResource(resource);
    if (resource === 'taxonomies' || resource === 'taxonomy_terms') return taxonomyProvider.getManyReference(resource, params);
    if (resource === 'org_units') return orgUnitsProvider.getManyReference(resource, params);
    if (isRestResource(resource)) return restProvider.getManyReference(resource, params);
    throw new Error(`No authorized API contract for resource: ${resource}`);
  },
  create: async (resource, params) => {
    rejectRetiredResource(resource);
    if (resource === 'taxonomies' || resource === 'taxonomy_terms') return taxonomyProvider.create(resource, params);
    if (resource === 'org_units') return orgUnitsProvider.create(resource, params);
    if (isRestResource(resource)) return restProvider.create(resource, params);
    throw new Error(`No authorized API contract for resource: ${resource}`);
  },
  update: async (resource, params) => {
    rejectRetiredResource(resource);
    if (resource === 'taxonomies' || resource === 'taxonomy_terms') return taxonomyProvider.update(resource, params);
    if (resource === 'org_units') return orgUnitsProvider.update(resource, params);
    if (isRestResource(resource)) return restProvider.update(resource, params);
    throw new Error(`No authorized API contract for resource: ${resource}`);
  },
  updateMany: async (resource, params) => {
    rejectRetiredResource(resource);
    if (resource === 'taxonomies' || resource === 'taxonomy_terms') return taxonomyProvider.updateMany(resource, params);
    if (resource === 'org_units') return orgUnitsProvider.updateMany(resource, params);
    if (isRestResource(resource)) return restProvider.updateMany(resource, params);
    throw new Error(`No authorized API contract for resource: ${resource}`);
  },
  delete: async (resource, params) => {
    rejectRetiredResource(resource);
    if (resource === 'taxonomies' || resource === 'taxonomy_terms') return taxonomyProvider.delete(resource, params);
    if (resource === 'org_units') return orgUnitsProvider.delete(resource, params);
    if (isRestResource(resource)) return restProvider.delete(resource, params);
    throw new Error(`No authorized API contract for resource: ${resource}`);
  },
  deleteMany: async (resource, params) => {
    rejectRetiredResource(resource);
    if (resource === 'taxonomies' || resource === 'taxonomy_terms') return taxonomyProvider.deleteMany(resource, params);
    if (resource === 'org_units') return orgUnitsProvider.deleteMany(resource, params);
    if (isRestResource(resource)) return restProvider.deleteMany(resource, params);
    throw new Error(`No authorized API contract for resource: ${resource}`);
  },
};

// React Admin passes one AbortSignal through a query's retry chain only when
// the provider opts in. REST reads use that signal as their stable operation
// identity for authorization-denial coalescing and cancellation.
dynamoProvider.supportAbortSignal = true;

export default dynamoProvider;
